import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Stack,
  Chip,
  Skeleton,
} from '@mui/material';
import { Calendar } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { leaveService } from '@/services/api';
import { LeaveType } from '@/types';

interface LeaveBalanceItem {
  type: LeaveType;
  total: number;
  used: number;
  pending: number;
  remaining: number;
}

const LeaveBalanceWidget: React.FC = () => {
  const { data: balanceResponse, isLoading, isError } = useQuery({
    queryKey: ['leaveBalance'],
    queryFn: () => leaveService.getLeaveBalance(),
    staleTime: 300000, // 5 minutes
  });

  const balances: LeaveBalanceItem[] = balanceResponse?.data?.balances ?? [];

  const getLeaveTypeLabel = (type: LeaveType): string => {
    const labels = {
      [LeaveType.CASUAL]: 'Casual',
      [LeaveType.SICK]: 'Sick',
      [LeaveType.EARNED]: 'Earned',
      [LeaveType.UNPAID]: 'Unpaid',
      [LeaveType.COMPENSATORY]: 'Comp Off',
    };
    return labels[type] || type;
  };

  const getLeaveTypeColor = (type: LeaveType): string => {
    const colors = {
      [LeaveType.CASUAL]: '#4F46E5',
      [LeaveType.SICK]: '#ef4444',
      [LeaveType.EARNED]: '#10b981',
      [LeaveType.UNPAID]: '#94a3b8',
      [LeaveType.COMPENSATORY]: '#8b5cf6',
    };
    return colors[type] || '#64748b';
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width={150} height={28} />
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={60} sx={{ mt: 2 }} />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Calendar size={20} style={{ color: '#64748b' }} />
          <Typography variant="h6" fontWeight={600}>
            Leave Balance
          </Typography>
        </Box>

        {balances.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No leave balance data available
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2.5}>
            {balances.map((balance) => {
              const usagePercent = balance.total > 0 
                ? Math.round((balance.used / balance.total) * 100) 
                : 0;
              const color = getLeaveTypeColor(balance.type);

              return (
                <Box key={balance.type}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {getLeaveTypeLabel(balance.type)}
                    </Typography>
                    <Chip
                      label={`${balance.remaining}/${balance.total}`}
                      size="small"
                      sx={{
                        bgcolor: `${color}20`,
                        color: color,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                      }}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={usagePercent}
                    sx={{
                      height: 8,
                      borderRadius: 1,
                      bgcolor: `${color}15`,
                      '& .MuiLinearProgress-bar': {
                        bgcolor: color,
                        borderRadius: 1,
                      },
                    }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Used: {balance.used} days
                    </Typography>
                    {balance.pending > 0 && (
                      <Typography variant="caption" color="warning.main">
                        Pending: {balance.pending}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default LeaveBalanceWidget;
